import { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, query } from 'firebase/firestore';
import { db } from '../../../firebaseConfig';
import { TABS } from './constants';
import {
  buildModeCardsFromRows,
  mapModeLabel,
  matchesSelectedPlayer,
  normalizeMode,
  normalizeText,
  sortByDateDesc,
} from './helpers';
import {
  getCachedPlayerSeasonEntries,
  setCachedPlayerSeasonEntries,
} from '../../../utils/playerStatsSessionCache';
import { calculateRecentTrend } from '../playerDashboardInsights';

export const usePlayerProfileData = ({
  playerId,
  playerName,
  playerStats = [],
  teamId,
  selectedSeason,
  seasonDocId,
  startDate,
  endDate,
  matchType,
  region,
}) => {
  const [activeTab, setActiveTab] = useState(TABS.FILTERED);
  const [seasonPlayerRows, setSeasonPlayerRows] = useState([]);
  const [matchHistory, setMatchHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [historyModeFilter, setHistoryModeFilter] = useState('ALL');
  const [historyMapFilter, setHistoryMapFilter] = useState('ALL');
  const [historyRangeFilter, setHistoryRangeFilter] = useState('ALL');
  const playerKey = playerId || playerName;

  const filteredStatsFromProps = useMemo(() => {
    const withinDate = (row) => {
      if (startDate && row?.Date && row.Date < startDate) return false;
      if (endDate && row?.Date && row.Date > endDate) return false;
      return true;
    };

    return (playerStats || []).filter((row) => {
      if (!matchesSelectedPlayer(row, playerName)) return false;
      if (matchType && normalizeText(row?.MatchType) !== normalizeText(matchType)) return false;
      if (region && normalizeText(row?.Region) !== normalizeText(region)) return false;
      if (!withinDate(row)) return false;
      return true;
    });
  }, [playerStats, playerName, startDate, endDate, matchType, region]);

  const filteredStatsFromSeason = useMemo(() => {
    const withinDate = (row) => {
      if (startDate && row?.Date && row.Date < startDate) return false;
      if (endDate && row?.Date && row.Date > endDate) return false;
      return true;
    };

    return (seasonPlayerRows || []).filter((row) => {
      if (matchType && normalizeText(row?.MatchType) !== normalizeText(matchType)) return false;
      if (region && normalizeText(row?.Region) !== normalizeText(region)) return false;
      if (!withinDate(row)) return false;
      return true;
    });
  }, [seasonPlayerRows, startDate, endDate, matchType, region]);

  const filteredStats = useMemo(() => (
    filteredStatsFromSeason.length > 0 ? filteredStatsFromSeason : filteredStatsFromProps
  ), [filteredStatsFromSeason, filteredStatsFromProps]);

  const filteredModeCards = useMemo(() => buildModeCardsFromRows(filteredStats), [filteredStats]);

  const filteredSummary = useMemo(() => {
    const uniqueGameKeys = new Set(
      filteredStats.map((row) =>
        row?.matchId ||
        row?.ModeID ||
        `${row?.Date || ''}|${normalizeMode(row?.Mode) || ''}|${row?.Map || ''}|${row?.VS || ''}`
      )
    );

    const totals = filteredStats.reduce(
      (acc, row) => ({
        k: acc.k + Number(row?.K || 0),
        d: acc.d + Number(row?.D || 0),
        games: acc.games + 1,
      }),
      { k: 0, d: 0, games: 0 }
    );

    const kd = totals.games ? (totals.d > 0 ? totals.k / totals.d : totals.k) : 0;
    return { totalGames: uniqueGameKeys.size || totals.games, overallKD: totals.games ? kd.toFixed(2) : null };
  }, [filteredStats]);

  const filteredGameHighs = useMemo(() => {
    const emptyHighs = { highestKD: '0.00', highestKills: 0, highestImpact: 0, highestAssists: 0, highestScore: 0 };
    const computeForRows = (rows) => {
      if (!rows.length) return emptyHighs;
      let highestKD = 0;
      let highestKills = 0;
      let highestImpact = 0;
      let highestAssists = 0;
      let highestScore = 0;
      rows.forEach((row) => {
        const kills = Number(row?.K || 0);
        const deaths = Number(row?.D || 0);
        const assists = Number(row?.A || 0);
        const score = Number(row?.Score || 0);
        const impact = Number(row?.Impact || 0);
        const kd = deaths > 0 ? kills / deaths : kills;
        if (kd > highestKD) highestKD = kd;
        if (kills > highestKills) highestKills = kills;
        if (impact > highestImpact) highestImpact = impact;
        if (assists > highestAssists) highestAssists = assists;
        if (score > highestScore) highestScore = score;
      });
      return { highestKD: highestKD.toFixed(2), highestKills, highestImpact, highestAssists, highestScore };
    };

    return {
      HP: computeForRows(filteredStats.filter((row) => normalizeMode(row?.Mode) === 'HP')),
      SND: computeForRows(filteredStats.filter((row) => normalizeMode(row?.Mode) === 'SND')),
      CTL: computeForRows(filteredStats.filter((row) => normalizeMode(row?.Mode) === 'CTL')),
      hasAny: filteredStats.length > 0,
    };
  }, [filteredStats]);

  const winLossSplit = useMemo(() => {
    const normalizeResult = (value) => {
      const raw = normalizeText(value);
      if (raw === 'WIN' || raw === 'W') return 'Win';
      if (raw === 'LOSS' || raw === 'LOSE' || raw === 'L') return 'Loss';
      return '';
    };
    const calc = (rows) => {
      if (!rows.length) return { kd: '--', score: '--' };
      const totals = rows.reduce((acc, row) => ({
        k: acc.k + Number(row?.K || 0),
        d: acc.d + Number(row?.D || 0),
        score: acc.score + Number(row?.Score || 0),
      }), { k: 0, d: 0, score: 0 });
      const kd = totals.d > 0 ? (totals.k / totals.d) : totals.k;
      return {
        kd: kd.toFixed(2),
        score: String(Math.round(totals.score / rows.length)),
      };
    };
    const wins = filteredStats.filter((row) => normalizeResult(row?.Result || row?.WinLose) === 'Win');
    const losses = filteredStats.filter((row) => normalizeResult(row?.Result || row?.WinLose) === 'Loss');
    return {
      wins: calc(wins),
      losses: calc(losses),
      winCount: wins.length,
      lossCount: losses.length,
    };
  }, [filteredStats]);

  const mapPerformance = useMemo(() => {
    const normalizeResult = (value) => {
      const raw = normalizeText(value);
      if (raw === 'WIN' || raw === 'W') return 'Win';
      if (raw === 'LOSS' || raw === 'LOSE' || raw === 'L') return 'Loss';
      return '';
    };
    const grouped = filteredStats.reduce((acc, row) => {
      const map = String(row?.Map || row?.map || '').trim();
      if (!map) return acc;
      if (!acc[map]) acc[map] = { map, matches: 0, wins: 0 };
      acc[map].matches += 1;
      if (normalizeResult(row?.Result || row?.WinLose) === 'Win') acc[map].wins += 1;
      return acc;
    }, {});
    return Object.values(grouped)
      .map((item) => ({ ...item, winPct: item.matches ? ((item.wins / item.matches) * 100).toFixed(0) : '0' }))
      .sort((a, b) => b.matches - a.matches);
  }, [filteredStats]);

  const operatorAnalytics = useMemo(() => {
    const grouped = filteredStats
      .filter((row) => {
        const mode = normalizeMode(row?.Mode || row?.mode);
        return mode === 'HP' || mode === 'CTL';
      })
      .reduce((acc, row) => {
        const op = String(row?.Operator || '').trim();
        if (!op || op === '-') return acc;
        if (!acc[op]) acc[op] = { operator: op, uses: 0, impactTotal: 0 };
        acc[op].uses += 1;
        acc[op].impactTotal += Number(row?.OperatorValue || 0);
        return acc;
      }, {});

    return Object.values(grouped)
      .map((item) => ({
        ...item,
        avgImpact: item.uses ? Math.round(item.impactTotal / item.uses) : 0,
      }))
      .sort((a, b) => b.uses - a.uses);
  }, [filteredStats]);

  const availableHistoryMaps = useMemo(() => {
    const source = historyModeFilter === 'ALL'
      ? matchHistory
      : matchHistory.filter((row) => normalizeMode(row?.Mode || row?.mode || row?.ModeID || row?.matchId) === historyModeFilter);
    return [...new Set(source.map((row) => row?.Map || row?.map).filter(Boolean))].sort();
  }, [matchHistory, historyModeFilter]);

  const filteredMatchHistory = useMemo(() => {
    let rows = [...matchHistory];
    if (historyModeFilter !== 'ALL') {
      rows = rows.filter((row) => normalizeMode(row?.Mode || row?.mode || row?.ModeID || row?.matchId) === historyModeFilter);
    }
    if (historyMapFilter !== 'ALL') {
      rows = rows.filter((row) => (row?.Map || row?.map || '') === historyMapFilter);
    }
    rows = sortByDateDesc(rows);
    if (historyRangeFilter !== 'ALL') {
      const limit = Number(historyRangeFilter);
      if (Number.isFinite(limit) && limit > 0) rows = rows.slice(0, limit);
    }
    return rows;
  }, [matchHistory, historyModeFilter, historyMapFilter, historyRangeFilter]);

  const historySummary = useMemo(() => {
    const rows = filteredMatchHistory || [];
    if (!rows.length) {
      return {
        avgKills: '--',
        avgDeaths: '--',
        avgAssists: '--',
        avgScore: '--',
        kd: '--',
      };
    }

    const totals = rows.reduce(
      (acc, row) => ({
        kills: acc.kills + Number(row?.K ?? row?.kills ?? 0),
        deaths: acc.deaths + Number(row?.D ?? row?.deaths ?? 0),
        assists: acc.assists + Number(row?.A ?? row?.assists ?? 0),
        score: acc.score + Number(row?.Score ?? row?.score ?? 0),
      }),
      { kills: 0, deaths: 0, assists: 0, score: 0 }
    );

    const avgKills = totals.kills / rows.length;
    const avgDeaths = totals.deaths / rows.length;
    const avgAssists = totals.assists / rows.length;
    const avgScore = totals.score / rows.length;
    const kdRaw = totals.deaths > 0 ? totals.kills / totals.deaths : totals.kills;

    return {
      avgKills: avgKills.toFixed(1),
      avgDeaths: avgDeaths.toFixed(1),
      avgAssists: avgAssists.toFixed(1),
      avgScore: String(Math.round(avgScore)),
      kd: kdRaw.toFixed(2),
    };
  }, [filteredMatchHistory]);

  const recentTrend = useMemo(() => calculateRecentTrend(filteredMatchHistory), [filteredMatchHistory]);

  useEffect(() => {
    if (historyMapFilter !== 'ALL' && !availableHistoryMaps.includes(historyMapFilter)) {
      setHistoryMapFilter('ALL');
    }
  }, [availableHistoryMaps, historyMapFilter]);

  useEffect(() => {
    setActiveTab(TABS.FILTERED);
    setHistoryModeFilter('ALL');
    setHistoryMapFilter('ALL');
    setHistoryRangeFilter('ALL');
  }, [playerKey]);

  useEffect(() => {
    const resolvedSeasonId = String(seasonDocId || selectedSeason || '');
    if (!teamId || !resolvedSeasonId || !playerName) {
      setSeasonPlayerRows([]);
      setMatchHistory([]);
      setHistoryLoading(false);
      setHistoryError('');
      return;
    }

    let cancelled = false;
    const fetchSeasonMatchHistory = async () => {
      setHistoryLoading(true);
      setHistoryError('');
      try {
        const entriesPath = `teams/${teamId}/seasons/${resolvedSeasonId}/database/playerStats/entries`;
        let flatEntries = getCachedPlayerSeasonEntries(teamId, resolvedSeasonId);
        if (!Array.isArray(flatEntries)) {
          const entriesSnap = await getDocs(query(collection(db, entriesPath)));
          flatEntries = entriesSnap.docs.map((entryDoc) => ({
            id: entryDoc.id,
            ...(entryDoc.data() || {}),
          }));
          setCachedPlayerSeasonEntries(teamId, resolvedSeasonId, flatEntries);
        }
        const rows = flatEntries
          .map((entry) => {
            if (!matchesSelectedPlayer(entry, playerName)) return null;
            const matchId = entry.matchId || entry.ModeID || '';
            return {
              ...entry,
              id: entry.id,
              matchId,
              Date: entry.Date || '',
              Mode: mapModeLabel(entry.Mode || normalizeMode(matchId)),
              K: Number(entry.K || 0),
              D: Number(entry.D || 0),
              A: Number(entry.A || 0),
              Score: Number(entry.Score || 0),
              Result: entry.Result || entry.WinLose || '',
              PF: Number(entry.PF ?? 0),
              PA: Number(entry.PA ?? 0),
            };
          })
          .filter(Boolean);
        if (cancelled) return;
        if (rows.length > 0) {
          setSeasonPlayerRows(rows);
          setMatchHistory(sortByDateDesc(rows));
        } else {
          setSeasonPlayerRows([]);
          setMatchHistory(sortByDateDesc(filteredStatsFromProps));
        }
      } catch (error) {
        if (cancelled) return;
        setSeasonPlayerRows([]);
        setMatchHistory(sortByDateDesc(filteredStatsFromProps));
        setHistoryError(error?.message || 'Failed to load match history.');
      } finally {
        if (!cancelled) setHistoryLoading(false);
      }
    };

    fetchSeasonMatchHistory();
    return () => { cancelled = true; };
  }, [teamId, selectedSeason, seasonDocId, playerName, filteredStatsFromProps]);

  return {
    activeTab, setActiveTab, filteredStats, filteredModeCards, filteredSummary, filteredGameHighs,
    historyLoading, historyError, historyModeFilter, setHistoryModeFilter, historyMapFilter,
    setHistoryMapFilter, historyRangeFilter, setHistoryRangeFilter, availableHistoryMaps, filteredMatchHistory,
    historySummary,
    recentTrend,
    winLossSplit,
    mapPerformance,
    operatorAnalytics,
  };
};
